import {
  Box,
  Center,
  Grid,
  HStack,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { statusOptions } from "../../../../hooks/useReservations";
import { ReservationResponse } from "@/repositories/ReservationRepository";

interface ReservationCalendarProps {
  reservations: ReservationResponse[];
  currentDate: Date;
  onDateChange: (date: Date) => void;
  onReservationClick: (reservation: ReservationResponse) => void;
}

export function ReservationCalendar({
  reservations,
  currentDate,
  onDateChange,
  onReservationClick,
}: ReservationCalendarProps) {
  const getDaysInMonth = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }, []);

  const getFirstDayOfMonth = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay(); // 0 for Sunday, 1 for Monday, etc.
  }, []);

  const formatDate = useCallback(
    (
      date: Date,
      options: Intl.DateTimeFormatOptions = { month: "long", year: "numeric" }
    ) => {
      return date.toLocaleDateString("pt-BR", options);
    },
    []
  );

  const getReservationsForDay = useCallback(
    (day: number) => {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      return reservations.filter((reservation) => {
        const checkIn = new Date(reservation.check_in_date + "T00:00:00");
        const checkOut = new Date(reservation.check_out_date + "T00:00:00");
        return date >= checkIn && date <= checkOut;
      });
    },
    [reservations, currentDate]
  );

  const nextMonth = useCallback(() => {
    onDateChange(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  }, [currentDate, onDateChange]);

  const prevMonth = useCallback(() => {
    onDateChange(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  }, [currentDate, onDateChange]);

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayIndex = getFirstDayOfMonth(currentDate);
    const days = [];
    const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

    // Week day headers
    weekDays.forEach((day) => {
      days.push(
        <Center
          key={`header-${day}`}
          py={2}
          fontWeight="bold"
          color="gray.600"
          borderBottom="1px"
          borderColor="gray.200"
          fontSize="sm"
        >
          {day}
        </Center>
      );
    });

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayIndex; i++) {
      days.push(<Box key={`empty-start-${i}`} />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayReservations = getReservationsForDay(day);
      days.push(
        <Box
          key={`day-${day}`}
          p={1.5}
          borderWidth={1}
          borderColor="gray.100"
          minH={{ base: "80px", md: "120px" }}
          position="relative"
          _hover={{ bg: "gray.50" }}
        >
          <Text
            position="absolute"
            top={1}
            right={1.5}
            fontSize="xs"
            color="gray.500"
            fontWeight="medium"
          >
            {day}
          </Text>
          <VStack
            spacing={1}
            mt={5}
            align="stretch"
            overflowY="auto"
            maxH={{ base: "60px", md: "90px" }}
          >
            {dayReservations.map((reservation) => (
              <Box
                key={reservation.id}
                bg={`${statusOptions[reservation.status]?.color || "gray"}.100`}
                p={1}
                borderRadius="sm"
                fontSize="xs"
                cursor="pointer"
                onClick={() => onReservationClick(reservation)}
                _hover={{ opacity: 0.8 }}
                title={`${reservation?.properties?.title} - ${reservation?.client?.name}`}
              >
                <Text
                  fontWeight="semibold"
                  noOfLines={1}
                  color={`${
                    statusOptions[reservation.status]?.color || "gray"
                  }.700`}
                >
                  {reservation?.properties?.title}
                </Text>
                <Text noOfLines={1} fontSize="xx-small" color="gray.600">
                  {reservation?.client?.name}
                </Text>
              </Box>
            ))}
          </VStack>
        </Box>
      );
    }

    // Fill remaining cells if the month doesn't end on a Saturday
    const totalCells = firstDayIndex + daysInMonth;
    const remainingCells = (7 - (totalCells % 7)) % 7;
    for (let i = 0; i < remainingCells; i++) {
      days.push(<Box key={`empty-end-${i}`} />);
    }

    return days;
  };

  return (
    <Box bg="white" borderRadius="lg" shadow="base" overflow="hidden">
      <HStack
        p={3}
        justify="space-between"
        borderBottomWidth={1}
        borderColor="gray.200"
      >
        <IconButton
          aria-label="Mês anterior"
          icon={<FiChevronLeft />}
          onClick={prevMonth}
          variant="ghost"
          size="sm"
        />
        <Text fontSize="md" fontWeight="semibold" color="gray.700">
          {formatDate(currentDate)}
        </Text>
        <IconButton
          aria-label="Próximo mês"
          icon={<FiChevronRight />}
          onClick={nextMonth}
          variant="ghost"
          size="sm"
        />
      </HStack>
      <Grid templateColumns="repeat(7, 1fr)" gap={0}>
        {renderCalendar()}
      </Grid>
    </Box>
  );
}
